
import { Helmet } from "react-helmet";
import { formatKeywordsString } from "@/utils/seoUtils";

export interface SeoMetadataProps {
  title: string;
  description: string;
  keywords: string[];
  canonicalUrl?: string;
  imageUrl?: string;
  ogType?: "website" | "article";
  language?: string;
  pagePath?: string;
  lastModified?: string;
}

const SeoMetadata = ({
  title,
  description,
  keywords = [],
  canonicalUrl,
  imageUrl = "https://lainasampo.fi/lainasampo-social-card.jpg",
  ogType = "website",
  language,
  pagePath,
  lastModified
}: SeoMetadataProps) => {
  // Ensure keywords is always an array
  const safeKeywords = Array.isArray(keywords) ? keywords : [];
  const keywordsString = formatKeywordsString(safeKeywords);
  const siteUrl = "https://lainasampo.fi";
  
  // Convert relative image URLs to absolute URLs for better SEO
  const absoluteImageUrl = imageUrl.startsWith('http') 
    ? imageUrl 
    : `${siteUrl}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;

  // Derive canonical URL from pagePath if not provided
  const finalCanonicalUrl = canonicalUrl || (pagePath ? `${siteUrl}${pagePath.startsWith('/') ? '' : '/'}${pagePath}` : undefined);

  // Detect if this is a preview/development environment
  const isPreviewEnvironment = 
    typeof window !== 'undefined' && 
    (window.location.hostname.includes('lovable.app') || 
     window.location.hostname.includes('lovable.dev') ||
     window.location.search.includes('preview=') ||
     window.location.search.includes('lovable='));

  // For logging (e.g., tracking page views or debugging)
  if (pagePath) {
    console.log(`SEO metadata for path: ${pagePath}`); 
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywordsString} />
      
      {/* Add noindex tag for preview/development environments */}
      {isPreviewEnvironment && <meta name="robots" content="noindex, nofollow" />}
      
      {finalCanonicalUrl && <link rel="canonical" href={finalCanonicalUrl} />}
      {language && <meta name="language" content={language} />}
      {lastModified && <meta name="last-modified" content={lastModified} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      {finalCanonicalUrl && <meta property="og:url" content={finalCanonicalUrl} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content="https://lainasampo.fi/lainasampo-social-card.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:alt" content="Lainasampo.fi - Kilpailuta lainasi ja säästä rahaa" />
      <meta property="og:site_name" content="Lainasampo.fi" />
      <meta property="og:locale" content="fi_FI" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      {finalCanonicalUrl && <meta property="twitter:url" content={finalCanonicalUrl} />}
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content="https://lainasampo.fi/lainasampo-social-card.jpg" />
      <meta property="twitter:image:alt" content="Lainasampo.fi - Kilpailuta lainasi ja säästä rahaa" />
      
      {/* Sitemap references */}
      <link rel="sitemap" type="application/xml" href="/sitemap.xml" />
      <link rel="sitemap" type="application/xml" href="/article-sitemap.xml" />

      {/* Performance optimization tags */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      
      {/* Add Apple touch icon */}
      <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="180x180" href="/apple-touch-icon.png" />
    </Helmet>
  );
};

export default SeoMetadata;
